import React from 'react';
import useBlessingClick from '../../hooks/useBlessingClick';

/**
 * Decorative flower motif (gold, 8-petal). Used near section headers
 * and feature blocks — mirrors the floral SVG sprinkled across
 * astroarunpandit.org for a premium spiritual feel.
 */
/**
 * OM symbol — clean SVG instead of emoji 🕉️.
 * Used as the brand mark in hero, footer, modal header.
 */
export const OmMark = ({ className = 'w-12 h-12' }) => (
  <svg
    viewBox="0 0 64 64"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    aria-hidden="true"
  >
    <text
      x="32"
      y="48"
      textAnchor="middle"
      fontFamily="'Tiro Devanagari Hindi', serif"
      fontSize="58"
      fontWeight="700"
      fill="currentColor"
    >
      ॐ
    </text>
  </svg>
);

export const FlowerMotif = ({ className = 'w-10 h-10' }) => (
  <svg
    viewBox="0 0 100 100"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    aria-hidden="true"
  >
    <g stroke="currentColor" strokeWidth="1.4" fill="none" opacity="0.9">
      {[0, 45, 90, 135].map((rot) => (
        <ellipse
          key={rot}
          cx="50"
          cy="50"
          rx="9"
          ry="36"
          transform={`rotate(${rot} 50 50)`}
        />
      ))}
      <circle cx="50" cy="50" r="6" fill="currentColor" opacity="0.6" />
    </g>
  </svg>
);

/**
 * Gold thin divider with center diamond — mirrors the section
 * separators on astroarunpandit's pages.
 */
export const GoldDivider = ({ className = '' }) => (
  <div
    className={`flex items-center justify-center gap-2 my-4 ${className}`}
    aria-hidden="true"
  >
    <span className="block h-px w-12 sm:w-20 bg-gradient-to-r from-transparent to-brand-gold" />
    <FlowerMotif className="w-5 h-5 text-brand-gold" />
    <span className="block h-px w-12 sm:w-20 bg-gradient-to-l from-transparent to-brand-gold" />
  </div>
);

/**
 * Section eyebrow + heading combo styled exactly like the reference site:
 *   tiny gold uppercase eyebrow → serif heading → gold divider.
 */
export const SectionTitle = ({
  eyebrow,
  title,
  subtitle,
  align = 'center',
  language = 'en',
}) => {
  const alignCls = align === 'center' ? 'text-center mx-auto' : 'text-left';
  const isHi = language === 'hi';
  return (
    <div className={`max-w-3xl ${alignCls}`}>
      {eyebrow && (
        <p className="text-brand-gold-dark uppercase tracking-[0.25em] text-xs sm:text-sm font-semibold mb-3">
          {eyebrow}
        </p>
      )}
      <h2
        className={`font-playfair font-bold text-3xl sm:text-4xl md:text-5xl text-brand-maroon leading-tight ${
          isHi ? 'font-hindi-serif' : ''
        }`}
      >
        {title}
      </h2>
      {align === 'center' && <GoldDivider />}
      {subtitle && (
        <p
          className={`text-brand-text-soft text-base sm:text-lg leading-relaxed mt-4 ${
            isHi ? 'font-hindi' : 'font-outfit'
          }`}
        >
          {subtitle}
        </p>
      )}
    </div>
  );
};

/**
 * Primary maroon-pill CTA used everywhere in the new design.
 * Accepts standard <button> or <a> via `as` prop.
 */
export const PremiumButton = ({
  children,
  variant = 'primary',
  size = 'md',
  as: Tag = 'button',
  className = '',
  onClick,
  ...rest
}) => {
  const blessing = useBlessingClick();
  const handleClick = (e) => {
    blessing(e);
    if (onClick) onClick(e);
  };
  const variants = {
    primary:
      'bg-brand-maroon text-white hover:bg-brand-maroon-dark shadow-maroon-soft hover:shadow-premium hover:-translate-y-0.5',
    gold:
      'bg-brand-gold text-brand-text-dark hover:bg-brand-gold-dark shadow-gold-glow hover:-translate-y-0.5',
    outline:
      'bg-transparent border-2 border-brand-maroon text-brand-maroon hover:bg-brand-maroon hover:text-white',
  };
  const sizes = {
    sm: 'px-5 py-2.5 text-sm',
    md: 'px-7 py-3.5 text-base',
    lg: 'px-9 py-4 text-lg',
  };
  return (
    <Tag
      onClick={handleClick}
      className={`inline-flex items-center justify-center gap-2 rounded-full font-semibold tracking-wide transition-all duration-200 ${variants[variant]} ${sizes[size]} ${className}`}
      {...rest}
    >
      {children}
    </Tag>
  );
};
